// src/components/AddPaymentMethodForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Select,
  MenuItem,
  ListSubheader,
  InputLabel,
  FormControl,
  FormHelperText,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Stack,
  Divider,
  Link,
  Checkbox,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuthContext } from "../context/AuthContext";
import { useCompanies } from "../hooks/useCompanies";
import { useTypes } from "../hooks/useTypes";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../constants/workspaceSegments";
import { fetchData } from "../api/client";
import { TIPO_OPCOES, isPixPaymentTipo } from "../constants/paymentMethodTipo";
import {
  isCardPaymentTipo,
  PAYMENT_CARD_BRAND_OPTIONS,
  parseBandeirasAceitasFromPaymentMethod,
} from "../constants/paymentCardBrands";
import { getPaymentMethodDocConfig } from "../constants/paymentMethodProviderDocs";
import CieloPixMtlsCsrPanel from "./paymentMethods/CieloPixMtlsCsrPanel";
import CieloPixCertFilesPanel from "./paymentMethods/CieloPixCertFilesPanel";
import PaymentMethodNotificationPanel from "./paymentMethods/PaymentMethodNotificationPanel";
import { deriveCieloMtlsPartnerSlug } from "../utils/cieloPixMtlsCsr";
import { useTranslation } from "react-i18next";

const GROUP_ORDER = [
  "Pix",
  "Cartão",
  "Link / Checkout",
  "AppMax (API)",
  "Outros",
];

/** Campos de mapeamento exclusivos de Pix expirado */
const PAY_UNPAID_FIELD_KEYS = [
  "pay_unpaid_type_id",
  "pay_unpaid_type1_id",
  "pay_unpaid_type2_id",
  "pay_unpaid_type3_id",
];

function payUnpaidFieldsNull() {
  return Object.fromEntries(PAY_UNPAID_FIELD_KEYS.map((k) => [k, null]));
}
const PAYMENT_BASE_TITLES = {
  pay_approved: "Pagamento aprovado",
  pay_pending: "Pagamento pendente",
  pay_refused: "Pagamento recusado",
  pay_unpaid: "Pagamento não feito",
};

/** Texto explicativo por base — ajuda a distinguir os momentos do fluxo */
const PAYMENT_BASE_DESCRIPTIONS = {
  pay_approved:
    "Quando o pagamento é confirmado pelo banco ou gateway.",
  pay_pending:
    "Enquanto o cliente ainda pode pagar dentro do prazo (Pix ativo, boleto em aberto, etc.).",
  pay_refused:
    "Quando o banco, o gateway ou o antifraude recusa ou cancela a transação.",
  pay_unpaid:
    "Quando o cliente gerou o Pix, mas não concluiu o pagamento dentro do prazo (código/QR expirado). " +
    "O pedido deixa de ser tratado como pendente e recebe o tipo/subtipo abaixo. " +
    "Diferente de Pagamento pendente (aguardando dentro do prazo) e Pagamento recusado (recusa do banco). " +
    "Aplica-se somente a Pix.",
};

/** Lista explícita das bases que queremos exibir (nome igual ao da API) */
const PAYMENT_BASES = ["pay_approved", "pay_pending", "pay_refused", "pay_unpaid"];

/** Helper de título genérico */
const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

const CIELO_KINDS = ["ec_api", "ec_portal", "gateway", "pix_api2", "bradesco_pix"];
const CIELO_KINDS_PIX_SET = new Set(["ec_api", "ec_portal", "gateway", "pix_api2", "bradesco_pix"]);
const CIELO_KINDS_CARD_SET = new Set(["ec_api", "ec_portal", "gateway"]);
/** Payment.Provider no Gateway Pix — valores documentados pela Cielo. */
const CIELO_PIX_GATEWAY_PROVIDERS = ["Cielo2", "Bradesco2", "Cielo30", "BancoDoBrasil3"];

function normalizeCieloPixGatewayProvider(value) {
  const raw = String(value || "Cielo2").trim();
  const hit = CIELO_PIX_GATEWAY_PROVIDERS.find((p) => p.toLowerCase() === raw.toLowerCase());
  return hit || "Cielo2";
}

/** Valor controlado do Select Cielo (evita display vazio se o estado não casar com nenhum MenuItem). */
function normalizeCieloIntegrationKindSelectValue(kind, isPixCieloForm) {
  const k = String(kind ?? "ec_api").trim();
  const allowed = isPixCieloForm ? CIELO_KINDS_PIX_SET : CIELO_KINDS_CARD_SET;
  if (allowed.has(k)) return k;
  return "ec_api";
}

/** Tipos Cielo API (cartão / Pix): integração e ambiente salvos em detalhes3 */
function deriveCieloUiFromPaymentMethod(pm) {
  const defaults = {
    cielo_integration_kind: "ec_api",
    cielo_sandbox: 0,
    cielo_custom_url: 0,
    cielo_pix_provider: "Cielo2",
    cielo_detalhes_extra: {},
  };
  const tipo = Number(pm?.tipo);
  if (tipo !== 14 && tipo !== 15) return defaults;

  let kind = "ec_api";
  let sandbox = false;
  let pixProvider = "Cielo2";
  let extra = {};
  let hasExplicitVersion = false;
  let hasDeclaredKind = false;
  try {
    const raw = pm?.detalhes3 != null ? String(pm.detalhes3).trim() : "";
    if (raw) {
      const j = JSON.parse(raw);
      if (j && typeof j === "object") {
        const declared = String(j.cielo_integration_kind || "").trim();
        if (declared && CIELO_KINDS.includes(declared)) {
          kind = declared;
          hasDeclaredKind = true;
          if (tipo === 14 && (kind === "pix_api2" || kind === "bradesco_pix")) {
            kind = "ec_api";
            hasDeclaredKind = false;
          }
        }
        hasExplicitVersion = Object.prototype.hasOwnProperty.call(j, "cieloApiVersion");
        const cav = Number(j.cieloApiVersion);
        if (j.sandbox === true) sandbox = true;
        pixProvider = normalizeCieloPixGatewayProvider(
          j.cieloPixProvider || j.cielo_pix_provider || j.cieloProvider || j.provider
        );
        const {
          cieloApiVersion: _cav,
          sandbox: _sb,
          cieloV1UrlMode: _vm,
          cielo_v1_url_mode: _vm2,
          cielo_integration_kind: _ik,
          cieloPixProvider: _pixProv,
          cielo_pix_provider: _pixProv2,
          cieloPixMtlsCsrPem: _csrPem,
          cieloPixMtlsCsrGeneratedAt: _csrAt,
          cieloPixMtlsPartnerSlug: _csrSlug,
          cieloPixMtlsCn: _csrCn,
          cieloPixMtlsCsrSource: _csrSrc,
          ...rest
        } = j;
        extra = rest;
        if (!hasDeclaredKind) {
          if (cav === 2) kind = "gateway";
          else if (j.cieloV1UrlMode === "portal" || j.cielo_v1_url_mode === "portal") kind = "ec_portal";
        }
      }
    }
  } catch {
    extra = {};
  }

  const urlFull = String(pm?.urlapi || "").trim();
  const url = urlFull.toLowerCase();
  if (!hasDeclaredKind && !hasExplicitVersion && url.includes("braspag")) kind = "gateway";
  if (!hasDeclaredKind && kind !== "gateway" && url.includes("/api/public")) kind = "ec_portal";
  if (url.includes("sandbox")) sandbox = true;

  return {
    cielo_integration_kind: kind,
    cielo_sandbox: sandbox ? 1 : 0,
    cielo_custom_url: urlFull ? 1 : 0,
    cielo_pix_provider: pixProvider,
    cielo_detalhes_extra: extra,
  };
}

function isAppmaxTipo(tipo) {
  return [11, 12, 13].includes(Number(tipo));
}

function parseAppmaxNotificationUrl(pm) {
  try {
    const raw = pm?.detalhes3 != null ? String(pm.detalhes3).trim() : "";
    if (!raw) return "";
    const j = JSON.parse(raw);
    if (!j || typeof j !== "object") return "";
    return String(j.notification_url || j.appmax_webhook_url || "").trim();
  } catch {
    return "";
  }
}

function mergeAppmaxDetalhes3(existingRaw, notificationUrl) {
  let extra = {};
  try {
    const raw = existingRaw != null ? String(existingRaw).trim() : "";
    if (raw) {
      const j = JSON.parse(raw);
      if (j && typeof j === "object") extra = { ...j };
    }
  } catch {
    extra = {};
  }
  const url = String(notificationUrl || "").trim();
  if (url) {
    extra.notification_url = url;
    extra.appmax_webhook_url = url;
  } else {
    delete extra.notification_url;
    delete extra.appmax_webhook_url;
  }
  return Object.keys(extra).length ? JSON.stringify(extra) : null;
}

/** Regras de obrigatoriedade/rotulagem por tipo */
function getFieldRulesByTipo(tipo, opts = {}) {
  const cieloKind = String(opts.cieloKind || "ec_api");
  const rules = {
    cieloIntegration: false,
    comuns: [
      "nome",
      "status",
      "tipo",
      "emitirbpe",
      "id_interno",
      "acrescimo",
      "taxa_retencao",
      "desconto",
      "parcelas_semjuros",
      "parcelas_comjuros",
      "tipo_calculo_juros",
      "juros_parcelamento",
      "valor_minimo_parcela_semjuros",
      "valor_minimo_parcela_comjuros",
    ],
    required: [],
    labels: {},
    helpers: {},
    show: new Set(),
  };
  rules.comuns.forEach((f) => rules.show.add(f));

  switch (Number(tipo)) {
    case 3: // Cielo (Checkout)
      rules.labels.client_id = "Client ID";
      rules.labels.client_secret = "Client Secret";
      rules.labels.urlapi = "Host da API (opcional)";
      rules.helpers.urlapi =
        "Checkout/Link da Cielo usa a base /api/public. Se precisar personalizar, use apenas o host/domínio (ex.: cieloecommerce.cielo.com.br ou apisandbox.cieloecommerce.cielo.com.br).";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 14: // Cielo (cartão API)
      rules.labels.client_id = "MerchantId";
      rules.labels.client_secret = "MerchantKey";
      rules.labels.urlapi = "Endereço personalizado (opcional)";
      rules.helpers.urlapi =
        "Só preencha se a Cielo tiver passado um host diferente do padrão. Use só o domínio, sem caminho no final.";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.cieloIntegration = true;
      break;

    case 6: // Mercado Pago
      rules.labels.client_id = "Public Key";
      rules.labels.client_secret = "Access Token";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      break;

    case 1: // PIX Buspay
      rules.labels.client_id = "Client ID";
      rules.labels.client_secret = "Client Secret";
      rules.labels.urlapi = "Host da API Pix";
      rules.labels.pix_auth_url = "URL do OAuth (opcional)";
      rules.helpers.urlapi = "Ex.: api-itape-pix.buspay.com.br/itapepix";
      rules.helpers.pix_auth_url = "Ex.: https://….auth.sa-east-1.amazoncognito.com/oauth2/token";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      rules.show.add("pix_auth_url");
      break;

    case 2: // Pagar.me
      rules.labels.client_secret = "Secret key (sk_…)";
      rules.labels.urlapi = "Base da API (opcional)";
      rules.helpers.urlapi = "Padrão: https://api.pagar.me/core/v5";
      rules.required.push("client_secret");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 4: // Pague Fácil
      rules.labels.client_id = "Usuário";
      rules.labels.client_secret = "Senha";
      rules.labels.urlapi = "URL da API";
      rules.helpers.urlapi = "Host informado pelo provedor, com /api/1.0/";
      rules.required.push("client_id", "client_secret", "urlapi");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 7: // Xpay — sem credenciais no formulário; habilitação só pelos campos comuns
      break;

    case 8: {
      // PIX Itaú
      rules.labels.client_id = "API Pix Client Id";
      rules.labels.client_secret = "API Pix Client Secret";
      rules.labels.pix_key = "Pix Key";
      rules.labels.pix_merchant_name = "Pix Merchant Name";
      rules.labels.pix_merchant_city = "Pix Merchant City";
      rules.labels.urlapi = "Base das APIs Pix (host)";
      rules.helpers.urlapi = "Ex.: https://secure.api.itau";
      rules.labels.pix_auth_url = "URL do OAuth (opcional)";
      rules.required.push(
        "client_id",
        "client_secret",
        "pix_key",
        "pix_merchant_name",
        "pix_merchant_city"
      );
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("pix_key");
      rules.show.add("pix_merchant_name");
      rules.show.add("pix_merchant_city");
      rules.show.add("urlapi");
      rules.show.add("pix_auth_url");
      // TLS editor
      rules.show.add("pix_tls_mode");
      rules.show.add("pix_cert_pem");
      rules.show.add("pix_key_pem");
      rules.show.add("pix_ca_pem");
      rules.show.add("pix_pfx_b64");
      rules.show.add("pix_pfx_pass");
      break;
    }

    case 15: // PIX Cielo (API)
      if (cieloKind === "pix_api2") {
        rules.labels.client_id = "Client ID (OAuth)";
        rules.labels.client_secret = "Client Secret";
        rules.labels.pix_key = "Chave Pix (recebimento)";
        rules.labels.urlapi = "Endereço personalizado (opcional)";
        rules.helpers.urlapi =
          "Deixe vazio para usar as bases oficiais (OAuth api2 + cielo-pix/v1). Só preencha se a Cielo indicar hosts diferentes.";
        rules.helpers.pix_key = "Chave aleatória transacional habilitada no portal Cielo para a API Pix.";
        rules.required.push("client_id", "client_secret", "pix_key");
        rules.show.add("client_id");
        rules.show.add("client_secret");
        rules.show.add("pix_key");
      } else if (cieloKind === "bradesco_pix") {
        rules.labels.client_id = "Client ID (OAuth)";
        rules.labels.client_secret = "Client Secret";
        rules.labels.pix_key = "Chave Pix (recebimento)";
        rules.labels.urlapi = "Endereço personalizado (opcional)";
        rules.helpers.urlapi =
          "Só se indicar outro host. Domínio apenas (ex.: https://qrpix.bradesco.com.br) — sem /v2/cob nem path de token.";
        rules.helpers.pix_key = "Chave Pix cadastrada no Bradesco para recebimento via API Pix.";
        rules.required.push("client_id", "client_secret", "pix_key");
        rules.show.add("client_id");
        rules.show.add("client_secret");
        rules.show.add("pix_key");
      } else {
        rules.labels.client_id = "MerchantId";
        rules.labels.client_secret = "MerchantKey";
        rules.labels.urlapi = "Endereço personalizado (opcional)";
        rules.helpers.urlapi =
          "Só se a Cielo indicar um host fora do padrão. Domínio apenas, sem caminho no final.";
        rules.required.push("client_id", "client_secret");
        rules.show.add("client_id");
        rules.show.add("client_secret");
      }
      rules.cieloIntegration = true;
      break;

    case 9: // Rede
      rules.labels.client_id = "Filiation";
      rules.labels.client_secret = "Token";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      break;

    case 10: // Adyen
      rules.labels.client_id = "API Key";
      rules.labels.client_secret = "Merchant Account";
      rules.labels.urlapi = "URL da API";
      rules.helpers.urlapi = "Ex.: https://pal-test.adyen.com (teste)";
      rules.required.push("client_id", "client_secret", "urlapi");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 11: // AppMax — link
    case 12: // AppMax — PIX
    case 13: {
      // AppMax — API v3 (access-token no body)
      const t = Number(tipo);
      rules.labels.client_secret = "Chave API (access-token)";
      rules.labels.urlapi = "Base da API (ex.: https://homolog.sandboxappmax.com.br/api/v3)";
      const base =
        "Ex.: https://homolog.sandboxappmax.com.br/api/v3 — use a base do ambiente (homolog/produção) indicada pela Appmax.";
      rules.helpers.urlapi =
        t === 11
          ? `${base} Fluxo deste cadastro: link/checkout (customer + order).`
          : t === 12
            ? `${base} Fluxo deste cadastro: PIX via API (CPF do pagador na geração da cobrança).`
            : `${base} Fluxo deste cadastro: cartão via API (dados do cartão na requisição).`;
      rules.required.push("client_secret");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;
    }

    default:
      break;
  }

  return rules;
}

const numberOrNull = (v) => {
  if (v === "" || v === null || typeof v === "undefined") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function parseIdsProjetosFromPaymentMethod(pm) {
  const raw = pm?.ids_projetos;
  if (Array.isArray(raw)) {
    return raw.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .replace(/[[\]\s]/g, "")
      .split(/[,;|]+/)
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  const legacy = Number(pm?.id_projeto);
  if (Number.isFinite(legacy) && legacy > 0) return [legacy];
  return [];
}

function normalizeMultiline(s) {
  return String(s || "").replace(/\r\n/g, "\n");
}

/** Valida texto colado como Cert PEM Cielo (não CSR). Retorna mensagem de erro ou null. */
function cieloPixCertPemIssue(raw) {
  const s = normalizeMultiline(raw).replace(/^\uFEFF/, "").trim();
  if (!s) return null;
  if (/BEGIN\s+CERTIFICATE\s+REQUEST/i.test(s)) {
    return "csr";
  }
  if (!/-----BEGIN\s+CERTIFICATE-----/i.test(s) || !/-----END\s+CERTIFICATE-----/i.test(s)) {
    return "format";
  }
  return null;
}

const AddPaymentMethodForm = ({ paymentMethod, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();

  // Carrega empresas/projetos para o select de Projeto
  const { data: companiesData, isLoading: loadingCompanies } = useCompanies(1, 500, "");
  const companies = companiesData?.companies || [];

  // Carrega árvore de tipos (para mapeamento de status de pagamento)
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rootTypes = typesData?.types || [];

  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const { t: tSettings } = useTranslation("settings");
  const showEmitirBPe =
    !isLoadingSegment && segment === WorkspaceSegments.PassengerTransport;

  const [errorMessage, setErrorMessage] = useState(null);
  const [pixCertInput, setPixCertInput] = useState("text");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);
  const [isValidatingCredentials, setIsValidatingCredentials] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [credentialValidation, setCredentialValidation] = useState(null);
  const [notification, setNotification] = useState(null);
  const credentialValidationAnchorRef = useRef(null);

  useEffect(() => {
    if (credentialValidation && credentialValidationAnchorRef.current) {
      credentialValidationAnchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [credentialValidation]);

  const [formData, setFormData] = useState({
    ...paymentMethod,
    id_usuario: paymentMethod?.id_usuario ?? userData?.id ?? null,
    id_usuario_ultima_alteracao: userData?.id ?? null,
    // Empresas: [] => disponível em todas (global)
    ids_projetos: parseIdsProjetosFromPaymentMethod(paymentMethod),
    status: typeof paymentMethod?.status === "number" ? paymentMethod.status : 1,
    default: typeof paymentMethod?.default === "number" ? paymentMethod.default : 0,
    emitirbpe: typeof paymentMethod?.emitirbpe === "number" ? paymentMethod.emitirbpe : 0,
    tipo:
      typeof paymentMethod?.tipo === "number" || typeof paymentMethod?.tipo === "string"
        ? Number(paymentMethod.tipo)
        : 0,
    id_interno: paymentMethod?.id_interno ?? "",
    acrescimo: paymentMethod?.acrescimo ?? "",
    taxa_retencao: paymentMethod?.taxa_retencao ?? "",
    desconto: paymentMethod?.desconto ?? "",
    parcelas_semjuros: paymentMethod?.parcelas_semjuros ?? "",
    parcelas_comjuros: paymentMethod?.parcelas_comjuros ?? "",
    tipo_calculo_juros:
      paymentMethod?.tipo_calculo_juros === "total" ? "total" : "ao_mes",
    juros_parcelamento: paymentMethod?.juros_parcelamento ?? "",
    valor_minimo_parcela_semjuros: paymentMethod?.valor_minimo_parcela_semjuros ?? "",
    valor_minimo_parcela_comjuros: paymentMethod?.valor_minimo_parcela_comjuros ?? "",
    client_id: paymentMethod?.client_id ?? "",
    client_secret: paymentMethod?.client_secret ?? "",
    urlapi: paymentMethod?.urlapi ?? "",
    pix_auth_url: paymentMethod?.pix_auth_url ?? "",

    // PIX (business)
    pix_key: paymentMethod?.pix_key ?? "",
    pix_merchant_name: paymentMethod?.pix_merchant_name ?? "",
    pix_merchant_city: paymentMethod?.pix_merchant_city ?? "",

    // TLS (novos)
    pix_tls_mode: paymentMethod?.pix_tls_mode ?? "", // 'pem' | 'pfx'
    pix_cert_pem: paymentMethod?.pix_cert_pem ?? "",
    pix_key_pem: paymentMethod?.pix_key_pem ?? "",
    pix_ca_pem: paymentMethod?.pix_ca_pem ?? "",
    pix_pfx_b64: paymentMethod?.pix_pfx_b64 ?? "",
    pix_pfx_pass: paymentMethod?.pix_pfx_pass ?? "",

    // Mapeamento de tipos por status de pagamento (já no padrão da API)
    pay_approved_type_id: paymentMethod?.pay_approved_type_id ?? null,
    pay_approved_type1_id: paymentMethod?.pay_approved_type1_id ?? null,
    pay_approved_type2_id: paymentMethod?.pay_approved_type2_id ?? null,
    pay_approved_type3_id: paymentMethod?.pay_approved_type3_id ?? null,

    pay_pending_type_id: paymentMethod?.pay_pending_type_id ?? null,
    pay_pending_type1_id: paymentMethod?.pay_pending_type1_id ?? null,
    pay_pending_type2_id: paymentMethod?.pay_pending_type2_id ?? null,
    pay_pending_type3_id: paymentMethod?.pay_pending_type3_id ?? null,

    pay_refused_type_id: paymentMethod?.pay_refused_type_id ?? null,
    pay_refused_type1_id: paymentMethod?.pay_refused_type1_id ?? null,
    pay_refused_type2_id: paymentMethod?.pay_refused_type2_id ?? null,
    pay_refused_type3_id: paymentMethod?.pay_refused_type3_id ?? null,

    pay_unpaid_type_id: paymentMethod?.pay_unpaid_type_id ?? null,
    pay_unpaid_type1_id: paymentMethod?.pay_unpaid_type1_id ?? null,
    pay_unpaid_type2_id: paymentMethod?.pay_unpaid_type2_id ?? null,
    pay_unpaid_type3_id: paymentMethod?.pay_unpaid_type3_id ?? null,

    bandeiras_aceitas: parseBandeirasAceitasFromPaymentMethod(paymentMethod),
    appmax_notification_url: parseAppmaxNotificationUrl(paymentMethod),

    ...deriveCieloUiFromPaymentMethod(paymentMethod),
  });

  // Se abriu o form (novo/edição), começamos "limpo" até o usuário alterar algo
  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [paymentMethod?.id]);

  /** Carrega o detalhe quando vier só { id } */
  useEffect(() => {
    const loadDetail = async () => {
      if (!paymentMethod?.id) return;
      setIsFetchingDetail(true);
      try {
        const resp = await fetchData(`/payment-methods/${paymentMethod.id}`, "GET");
        const m = resp?.data || {};
        setFormData((prev) => {
          const cielo = deriveCieloUiFromPaymentMethod(m);
          return {
            ...prev,
            ...m,
            // nunca reidrata segredos; flags has_* vêm em m e são mantidas em prev
            client_id: "",
            client_secret: "",
            pix_key: "",
            pix_cert_pem: "",
            pix_key_pem: "",
            pix_ca_pem: "",
            pix_pfx_b64: "",
            pix_pfx_pass: "",
            pix_auth_url: m.pix_auth_url ?? "",
            tipo: typeof m.tipo === "number" || typeof m.tipo === "string" ? Number(m.tipo) : 0,
            tipo_calculo_juros: m.tipo_calculo_juros === "total" ? "total" : "ao_mes",
            status: Number(m.status) === 1 ? 1 : 0,
            default: Number(m.default) === 1 ? 1 : 0,
            emitirbpe: Number(m.emitirbpe) === 1 ? 1 : 0,
            ids_projetos: parseIdsProjetosFromPaymentMethod(m),
            cielo_integration_kind: cielo.cielo_integration_kind,
            cielo_sandbox: cielo.cielo_sandbox,
            cielo_custom_url: cielo.cielo_custom_url,
            cielo_pix_provider: cielo.cielo_pix_provider,
            cielo_detalhes_extra: cielo.cielo_detalhes_extra,
            urlapi: m.urlapi ?? "",
            bandeiras_aceitas: parseBandeirasAceitasFromPaymentMethod(m),
            appmax_notification_url: parseAppmaxNotificationUrl(m),
          };
        });
        setCredentialValidation(null);
        setHasUnsavedChanges(false);
      } catch (e) {
        setErrorMessage("Não foi possível carregar os dados da forma de pagamento.");
      } finally {
        setIsFetchingDetail(false);
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod?.id]);

  useEffect(() => {
    const tipo = Number(formData.tipo);
    if (!Number.isFinite(tipo)) {
      setNotification(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchData(`/payment-methods/notification?tipo=${encodeURIComponent(tipo)}`, "GET");
        if (!cancelled) setNotification(resp?.data || null);
      } catch {
        if (!cancelled) setNotification(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.tipo]);

  const rules = useMemo(
    () => getFieldRulesByTipo(formData.tipo, { cieloKind: formData.cielo_integration_kind }),
    [formData.tipo, formData.cielo_integration_kind]
  );

  useEffect(() => {
    if (!rules.cieloIntegration) return;
    const tipoN = Number(formData.tipo);
    if (tipoN !== 14 && tipoN !== 15) return;
    const allowed = tipoN === 15 ? CIELO_KINDS_PIX_SET : CIELO_KINDS_CARD_SET;
    const k = String(formData.cielo_integration_kind ?? "").trim();
    if (!allowed.has(k)) {
      setFormData((p) => ({ ...p, cielo_integration_kind: "ec_api" }));
    }
  }, [rules.cieloIntegration, formData.tipo, formData.cielo_integration_kind]);

  const podeTestarCredenciais =
    rules.show.has("client_id") || rules.show.has("client_secret");

  const providerDocConfig = useMemo(
    () =>
      getPaymentMethodDocConfig(Number(formData.tipo), {
        cieloKind: String(formData.cielo_integration_kind || "ec_api"),
      }),
    [formData.tipo, formData.cielo_integration_kind]
  );

  const isPixCieloForm = Number(formData.tipo) === 15;
  const isCardPaymentForm = isCardPaymentTipo(formData.tipo);

  const cieloEffectiveBaseHint = useMemo(() => {
    if (!rules.cieloIntegration) return "";
    const kind = String(formData.cielo_integration_kind || "ec_api");
    const s = Number(formData.cielo_sandbox) === 1;
    if (isPixCieloForm) {
      if (kind === "pix_api2") {
        return s
          ? tSettings("paymentMethodsPage.cieloPixBase_api2_sandbox")
          : tSettings("paymentMethodsPage.cieloPixBase_api2_prod");
      }
      if (kind === "bradesco_pix") {
        return s
          ? tSettings("paymentMethodsPage.cieloPixBase_bradesco_sandbox")
          : tSettings("paymentMethodsPage.cieloPixBase_bradesco_prod");
      }
      if (kind === "gateway") {
        return s
          ? tSettings("paymentMethodsPage.cieloPixBase_gateway_sandbox")
          : tSettings("paymentMethodsPage.cieloPixBase_gateway_prod");
      }
      if (kind === "ec_portal") {
        return s
          ? tSettings("paymentMethodsPage.cieloPixBase_portal_sandbox")
          : tSettings("paymentMethodsPage.cieloPixBase_portal_prod");
      }
      return s
        ? tSettings("paymentMethodsPage.cieloPixBase_ecApi_sandbox")
        : tSettings("paymentMethodsPage.cieloPixBase_ecApi_prod");
    }
    if (kind === "gateway") {
      return s
        ? "Padrão: apisandbox.braspag.com.br (Gateway, homologação)."
        : "Padrão: api.braspag.com.br (Gateway, produção).";
    }
    if (kind === "ec_portal") {
      return s
        ? "Padrão: POST em apisandbox.cieloecommerce.cielo.com.br/api/public com OAuth (token …/api/public/v2/token) e Bearer nas vendas; consulta de venda na mesma base /api/public."
        : "Padrão: POST em cieloecommerce.cielo.com.br/api/public com OAuth (token …/api/public/v2/token) e Bearer nas vendas; consulta de venda na mesma base /api/public.";
    }
    return s
      ? "Padrão: apisandbox.cieloecommerce.cielo.com.br (API direta, homologação)."
      : "Padrão: api.cieloecommerce.cielo.com.br (API direta, produção).";
  }, [rules.cieloIntegration, formData.cielo_integration_kind, formData.cielo_sandbox, formData.tipo, tSettings]);
  const labelFor = useCallback(
    (field, fallback) => {
      const tipo = Number(formData.tipo);
      const kind = String(formData.cielo_integration_kind || "ec_api");
      if (tipo === 15 && (kind === "pix_api2" || kind === "bradesco_pix")) {
        if (field === "client_id") return "Client ID (OAuth)";
        if (field === "client_secret") return "Client Secret";
      }
      if ((tipo === 14 || tipo === 15) && kind === "ec_portal") {
        if (field === "client_id") return "Client ID (OAuth)";
        if (field === "client_secret") return "Client Secret (OAuth)";
      }
      return (rules.labels && rules.labels[field]) || fallback;
    },
    [rules.labels, formData.tipo, formData.cielo_integration_kind]
  );

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const isFieldRequired = (field) => rules.required.includes(field) && !hasStoredSecret(field);

  const maskSecretField = (field) => {
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const handleBandeiraToggle = (slug) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => {
      const current = Array.isArray(prev.bandeiras_aceitas) ? prev.bandeiras_aceitas : [];
      const next = current.includes(slug)
        ? current.filter((s) => s !== slug)
        : [...current, slug];
      return { ...prev, bandeiras_aceitas: next };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => ({
      ...prev,
      [name]: [
        "status",
        "default",
        "emitirbpe",
        "tipo",
        "cielo_sandbox",
        "cielo_custom_url",
      ].includes(name)
        ? Number(value)
        : value,
    }));
  };

  const handleFileToBase64 = async (file) => {
    const readAsDataURL = (f) =>
      new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
    const dataUrl = await readAsDataURL(file);
    const base64 = String(dataUrl).split(",")[1] || "";
    return base64;
  };

  /** ======= Helpers de cascata de tipos para status de pagamento ======= */
  const getCascadeLevels = (base) => {
    const rootVal = formData[`${base}_type_id`];
    const root = rootTypes.find((t) => Number(t.id) === Number(rootVal));
    const l1 = Array.isArray(root?.children) ? root.children : [];

    const sub1Val = formData[`${base}_type1_id`];
    const n1 = l1.find((s) => Number(s.id) === Number(sub1Val));
    const l2 = Array.isArray(n1?.children) ? n1.children : [];

    const sub2Val = formData[`${base}_type2_id`];
    const n2 = l2.find((u) => Number(u.id) === Number(sub2Val));
    const l3 = Array.isArray(n2?.children) ? n2.children : [];

    return { l1, l2, l3 };
  };

  const handleCascadeChange = (base, level, val) => {
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => {
      const next = { ...prev };
      if (level === 0) {
        next[`${base}_type_id`] = val === "" ? null : val;
        next[`${base}_type1_id`] = null;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 1) {
        next[`${base}_type1_id`] = val === "" ? null : val;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 2) {
        next[`${base}_type2_id`] = val === "" ? null : val;
        next[`${base}_type3_id`] = null;
      }
      if (level === 3) {
        next[`${base}_type3_id`] = val === "" ? null : val;
      }
      return next;
    });
  };

  const renderCascade = (base) => {
    const titulo = PAYMENT_BASE_TITLES[base] ?? base;
    const descricao = PAYMENT_BASE_DESCRIPTIONS[base] ?? null;
    const { l1, l2, l3 } = getCascadeLevels(base);

    return (
      <React.Fragment key={base}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {titulo}
          </Typography>
          {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
              {descricao}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Tipo"
            value={formData[`${base}_type_id`] ?? ""}
            onChange={(e) => handleCascadeChange(base, 0, e.target.value)}
            disabled={loadingTypes}
          >
            <MenuItem value="">—</MenuItem>
            {!loadingTypes &&
              rootTypes.map((t) => (
                <MenuItem key={`${base}-root-${t.id}`} value={t.id}>
                  {titleOf(t)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {l1.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 1"
              value={formData[`${base}_type1_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 1, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l1.map((s) => (
                <MenuItem key={`${base}-l1-${s.id}`} value={s.id}>
                  {titleOf(s)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l2.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 2"
              value={formData[`${base}_type2_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 2, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l2.map((u) => (
                <MenuItem key={`${base}-l2-${u.id}`} value={u.id}>
                  {titleOf(u)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l3.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 3"
              value={formData[`${base}_type3_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 3, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l3.map((v) => (
                <MenuItem key={`${base}-l3-${v.id}`} value={v.id}>
                  {titleOf(v)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </React.Fragment>
    );
  };

  /** Bases que realmente existem no formData (pay_unpaid só para Pix) */
  const cascadeBases = useMemo(
    () =>
      PAYMENT_BASES.filter((b) => {
        if (!Object.prototype.hasOwnProperty.call(formData, `${b}_type_id`)) return false;
        if (b === "pay_unpaid" && !isPixPaymentTipo(formData.tipo)) return false;
        return true;
      }),
    [formData]
  );

  /** Validação com lista de erros (para bloquear o botão Salvar) */
  const errors = useMemo(() => {
    const list = [];
    const nome = String(formData.nome || "").trim();
    if (!nome || nome.length < 3) list.push("Informe um nome com ao menos 3 caracteres.");
    if (formData.tipo === null || typeof formData.tipo === "undefined") list.push("Selecione um tipo.");

    const isActive = Number(formData.status) === 1;

    // obrigatórios por tipo (base) — rascunho inativo pode ser salvo e completado depois
    if (isActive) {
      rules.required.forEach((f) => {
        const v = String(formData[f] ?? "").trim();
        // em edição, se já existir um valor armazenado no backend (has_* = true),
        // não exigimos que o usuário reenvie o segredo
        if (!v && !hasStoredSecret(f)) list.push(`Campo obrigatório: ${labelFor(f, f)}.`);
      });
    }

    // mTLS PIX Itaú
    if (Number(formData.tipo) === 8 && isActive) {
      const mode = String(formData.pix_tls_mode || "").toLowerCase();
      const hasPemPair = !!formData.has_pix_cert_pem && !!formData.has_pix_key_pem;
      const hasPfxPair = !!formData.has_pix_pfx_b64 && !!formData.has_pix_pfx_pass;

      if (mode === "pem") {
        const hasTypedCert = String(formData.pix_cert_pem || "").trim() !== "";
        const hasTypedKey = String(formData.pix_key_pem || "").trim() !== "";
        if (!(hasTypedCert && hasTypedKey) && !hasPemPair) {
          list.push(tSettings("paymentMethodsPage.pixItauPemRequired"));
        }
      } else if (mode === "pfx") {
        const hasTypedFile = String(formData.pix_pfx_b64 || "").trim() !== "";
        const hasTypedPass = String(formData.pix_pfx_pass || "").trim() !== "";
        if (!(hasTypedFile && hasTypedPass) && !hasPfxPair) {
          list.push(tSettings("paymentMethodsPage.pixItauPfxRequired"));
        }
      } else {
        // só exige modo TLS se a forma estiver ativa e não houver nada salvo
        if (!hasPemPair && !hasPfxPair) {
          list.push("PIX Itaú: selecione o modo TLS (PEM ou PFX).");
        }
      }
    }

    // mTLS Pix Cielo API Pix / Bradesco API (produção)
    const isCieloPixApi2Form =
      Number(formData.tipo) === 15 && String(formData.cielo_integration_kind || "") === "pix_api2";
    const isBradescoPixForm =
      Number(formData.tipo) === 15 && String(formData.cielo_integration_kind || "") === "bradesco_pix";
    // Bradesco exige mTLS também no sandbox; api2 Cielo só em produção.
    const needsCieloStyleMtls =
      isBradescoPixForm || (isCieloPixApi2Form && Number(formData.cielo_sandbox) !== 1);
    if (needsCieloStyleMtls && isActive) {
      const hasCert =
        String(formData.pix_cert_pem || "").trim() !== "" || !!formData.has_pix_cert_pem;
      const hasKey =
        String(formData.pix_key_pem || "").trim() !== "" || !!formData.has_pix_key_pem;
      if (!hasCert || !hasKey) {
        list.push(
          isBradescoPixForm
            ? tSettings("paymentMethodsPage.cieloPixBradescoMtlsRequired", {
                defaultValue:
                  "Pix Bradesco: informe certificado e chave privada mTLS (PEM) do onboarding — obrigatório também em homologação.",
              })
            : tSettings("paymentMethodsPage.cieloPixApi2MtlsRequired", {
                defaultValue:
                  "Pix Cielo (produção): envie os arquivos .cer e .pfx, ou cole o certificado que a Cielo enviou.",
              })
        );
      } else {
        const typedCert = String(formData.pix_cert_pem || "").trim();
        const certIssue = typedCert ? cieloPixCertPemIssue(typedCert) : null;
        if (certIssue === "csr") {
          list.push(
            tSettings("paymentMethodsPage.cieloPixApi2CertIsCsr", {
              defaultValue:
                "Você colou o CSR (solicitação), não o certificado. Cole o texto com -----BEGIN CERTIFICATE----- enviado pela Cielo.",
            })
          );
        } else if (certIssue === "format") {
          list.push(
            tSettings("paymentMethodsPage.cieloPixApi2CertInvalidPem", {
              defaultValue:
                "Certificado inválido: precisa incluir -----BEGIN CERTIFICATE----- e -----END CERTIFICATE-----.",
            })
          );
        }
      }
    }
    return list;
  }, [formData, rules, tSettings]);

  const canSubmit = errors.length === 0 && !isSubmitting && !isLoading && !isFetchingDetail;

  const buildApiPayload = () => {
    const tipoNum = Number(formData.tipo);
    let detalhes3Json = null;
    if (tipoNum === 14 || tipoNum === 15) {
      const extra =
        formData.cielo_detalhes_extra && typeof formData.cielo_detalhes_extra === "object"
          ? { ...formData.cielo_detalhes_extra }
          : {};
      delete extra.cieloApiVersion;
      delete extra.sandbox;
      delete extra.cieloV1UrlMode;
      delete extra.cielo_v1_url_mode;
      delete extra.cielo_integration_kind;
      const kind = String(formData.cielo_integration_kind || "ec_api");
      if (tipoNum === 15) {
        const allowed = new Set([
          "timeoutMs",
          "customerName",
          "pix_customer_identity",
          "pix_customer_identity_type",
          "cieloPortalAuth",
        ]);
        if (kind === "gateway") {
          allowed.add("expirationSegundos");
          allowed.add("expirationSec");
          allowed.add("expiration_sec");
          allowed.add("qrCodeExpiration");
          allowed.add("cieloPixProvider");
        }
        if (kind === "pix_api2") {
          allowed.add("cieloPixCobExpiracaoSegundos");
          allowed.add("cieloPixOAuthBaseUrl");
          allowed.add("cieloPixResourceBaseUrl");
          // CSR mTLS gerado no formulário (antes de ter id) — persistir no 1º save
          allowed.add("cieloPixMtlsCsrPem");
          allowed.add("cieloPixMtlsCsrGeneratedAt");
          allowed.add("cieloPixMtlsPartnerSlug");
          allowed.add("cieloPixMtlsCn");
          allowed.add("cieloPixMtlsCsrSource");
        }
        if (kind === "bradesco_pix") {
          allowed.add("cieloPixCobExpiracaoSegundos");
          allowed.add("cieloPixOAuthBaseUrl");
          allowed.add("cieloPixResourceBaseUrl");
          allowed.add("bradescoPixBaseUrl");
          allowed.add("bradescoPixOAuthScope");
          allowed.add("bradescoPixCobExpiracaoSegundos");
        }
        Object.keys(extra).forEach((k) => {
          if (!allowed.has(k)) delete extra[k];
        });
        if (kind === "gateway") {
          extra.cieloPixProvider = normalizeCieloPixGatewayProvider(formData.cielo_pix_provider);
        } else {
          delete extra.cieloPixProvider;
          delete extra.cielo_pix_provider;
          delete extra.expirationSegundos;
          delete extra.expirationSec;
          delete extra.expiration_sec;
          delete extra.qrCodeExpiration;
        }
      }
      const cieloApiVersion = kind === "gateway" ? 2 : 1;
      const d3 = {
        ...extra,
        cielo_integration_kind: kind,
        cieloApiVersion,
        sandbox: Number(formData.cielo_sandbox) === 1,
      };
      if (cieloApiVersion === 1) {
        d3.cieloV1UrlMode = kind === "ec_portal" ? "portal" : "api";
      }
      detalhes3Json = JSON.stringify(d3);
    } else if (isAppmaxTipo(tipoNum)) {
      detalhes3Json = mergeAppmaxDetalhes3(formData.detalhes3, formData.appmax_notification_url);
    }

    const urlapiValue =
      tipoNum === 14 || tipoNum === 15
        ? Number(formData.cielo_custom_url) === 1
          ? String(formData.urlapi || "").trim() || null
          : null
        : String(formData.urlapi || "").trim() || null;

    const { notification: _omitNotification, ...formWithoutNotification } = formData;
    const payload = {
      ...formWithoutNotification,
      nome: String(formData.nome || "").trim(),
      tipo: tipoNum,
      ids_projetos: Array.isArray(formData.ids_projetos)
        ? formData.ids_projetos.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0)
        : [],
      acrescimo: numberOrNull(formData.acrescimo),
      taxa_retencao: numberOrNull(formData.taxa_retencao),
      desconto: numberOrNull(formData.desconto),
      parcelas_semjuros: numberOrNull(formData.parcelas_semjuros),
      parcelas_comjuros: numberOrNull(formData.parcelas_comjuros),
      tipo_calculo_juros: formData.tipo_calculo_juros === "total" ? "total" : "ao_mes",
      juros_parcelamento: numberOrNull(formData.juros_parcelamento),
      valor_minimo_parcela_semjuros: numberOrNull(formData.valor_minimo_parcela_semjuros),
      valor_minimo_parcela_comjuros: numberOrNull(formData.valor_minimo_parcela_comjuros),
      status: Number(formData.status) === 1 ? 1 : 0,
      default: Number(formData.default) === 1 ? 1 : 0,
      emitirbpe: Number(formData.emitirbpe) === 1 ? 1 : 0,
      id_interno: String(formData.id_interno || "").trim() || null,
      client_id: String(formData.client_id || "").trim() || null,
      client_secret: String(formData.client_secret || "").trim() || null,
      urlapi: urlapiValue,
      pix_auth_url: String(formData.pix_auth_url || "").trim() || null,

      pix_key: String(formData.pix_key || "").trim() || null,
      pix_merchant_name: String(formData.pix_merchant_name || "").trim() || null,
      pix_merchant_city: String(formData.pix_merchant_city || "").trim() || null,

      pix_tls_mode: String(formData.pix_tls_mode || "").trim() || null,
      pix_cert_pem: normalizeMultiline(formData.pix_cert_pem || "") || null,
      pix_key_pem: normalizeMultiline(formData.pix_key_pem || "") || null,
      pix_ca_pem: normalizeMultiline(formData.pix_ca_pem || "") || null,
      pix_pfx_b64: String(formData.pix_pfx_b64 || "").trim() || null,
      pix_pfx_pass: String(formData.pix_pfx_pass || "").trim() || null,

      detalhes1: null,
      detalhes2: null,
      detalhes3: tipoNum === 14 || tipoNum === 15 || isAppmaxTipo(tipoNum) ? detalhes3Json : null,
      bandeiras_aceitas: isCardPaymentTipo(tipoNum)
        ? (Array.isArray(formData.bandeiras_aceitas) ? formData.bandeiras_aceitas : [])
        : [],
      ...(isPixPaymentTipo(tipoNum)
        ? {}
        : payUnpaidFieldsNull()),
    };

    if (tipoNum === 7) {
      payload.client_id = null;
      payload.client_secret = null;
    }

    Object.keys(payload).forEach((k) => {
      if (/_type(?:[123])?_id$/.test(k)) {
        const v = payload[k];
        if (v === "" || v === null || typeof v === "undefined") {
          payload[k] = null;
        } else {
          const n = Number(v);
          payload[k] = Number.isFinite(n) ? n : null;
        }
      }
    });

    delete payload.cielo_integration_kind;
    delete payload.cielo_sandbox;
    delete payload.cielo_custom_url;
    delete payload.cielo_pix_provider;
    delete payload.cielo_detalhes_extra;
    delete payload.appmax_notification_url;

    return payload;
  };

  const handleValidateCredentials = async () => {
    if (!podeTestarCredenciais) return;
    setIsValidatingCredentials(true);
    setCredentialValidation(null);
    setErrorMessage(null);
    try {
      const payload = buildApiPayload();
      if (formData?.id) payload.id = formData.id;
      const resp = await fetchData("/payment-methods/validate", "POST", payload);
      const r = resp?.data;
      if (r && typeof r === "object" && "valid" in r) {
        const severity =
          r.severity === "success" ||
          r.severity === "error" ||
          r.severity === "warning" ||
          r.severity === "info"
            ? r.severity
            : r.valid
              ? "success"
              : r.outcome === "auth_failed"
                ? "error"
                : "warning";
        setCredentialValidation({
          valid: !!r.valid,
          severity,
          outcome: r.outcome || null,
          confidence: r.confidence || null,
          title: r.userTitle || (r.valid ? "Ok" : "Atenção"),
          detail: r.userDetail || "",
          nextSteps: Array.isArray(r.nextSteps) ? r.nextSteps : [],
          technicalDetail: r.technicalDetail || "",
        });
      } else {
        setCredentialValidation({
          valid: false,
          severity: "warning",
          title: "Não entendemos a resposta do teste",
          detail: "Tente novamente. Se o problema continuar, fale com o suporte e envie um print desta tela.",
          nextSteps: [],
        });
      }
    } catch (error) {
      const msg =
        (error && typeof error === "object" && error.message) ||
        error?.response?.data?.message ||
        (typeof error === "string" ? error : null) ||
        "Não foi possível concluir o teste agora.";
      setCredentialValidation({
        valid: false,
        severity: "warning",
        title: "Não foi possível testar agora",
        detail: String(msg),
        nextSteps: [
          "Verifique se todos os campos obrigatórios estão preenchidos.",
          "Tente novamente em alguns instantes.",
        ],
      });
    } finally {
      setIsValidatingCredentials(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      if (errors.length) throw new Error(errors.join(" "));

      const payload = buildApiPayload();
      const resp = await onSubmit(payload);
      const savedId =
        resp?.data?.id ??
        resp?.data?.data?.id ??
        payload?.id ??
        null;
      if (savedId && !formData?.id) {
        setFormData((p) => ({ ...p, id: savedId }));
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || "Erro ao salvar a forma de pagamento."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPixItau = Number(formData.tipo) === 8;
  const isCieloPixApi2 =
    Number(formData.tipo) === 15 && String(formData.cielo_integration_kind || "") === "pix_api2";
  const isBradescoPix =
    Number(formData.tipo) === 15 && String(formData.cielo_integration_kind || "") === "bradesco_pix";
  const showPixMtlsPanel = isPixItau || isCieloPixApi2 || isBradescoPix;
  const isCieloPixProd = isCieloPixApi2 && Number(formData.cielo_sandbox) !== 1;
  // Bradesco: mTLS em sandbox e produção (host prebanco exige client cert).
  const showBradescoMtls = isBradescoPix;
  const showCieloStyleMtls = isCieloPixProd || showBradescoMtls;

  const cieloMtlsPartner = useMemo(() => {
    let workspaceSlug = "";
    try {
      workspaceSlug =
        typeof localStorage !== "undefined"
          ? String(localStorage.getItem("company") || "").trim()
          : "";
    } catch {
      workspaceSlug = "";
    }
    const derived = deriveCieloMtlsPartnerSlug({
      companies,
      projectId: Array.isArray(formData.ids_projetos) && formData.ids_projetos.length
        ? formData.ids_projetos[0]
        : null,
      workspaceCompanySlug: workspaceSlug,
      paymentMethodName: formData.nome,
      idInterno: formData.id_interno || formData.idInterno,
    });
    let label = "";
    const firstProjectId =
      Array.isArray(formData.ids_projetos) && formData.ids_projetos.length
        ? Number(formData.ids_projetos[0])
        : null;
    if (derived.source === "project" && firstProjectId) {
      const c = companies.find((x) => Number(x.id) === Number(firstProjectId));
      label = c?.nome || c?.fantasia || c?.razao_social || "";
    } else if (derived.source === "workspace") {
      const c = companies.find((x) => {
        const keys = [x.slug, x.chave, x.domain, x.company, x.identificador]
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase());
        return keys.includes(workspaceSlug.toLowerCase());
      });
      label = c?.nome || c?.fantasia || c?.razao_social || workspaceSlug;
    } else if (derived.source === "paymentMethodName") {
      label = formData.nome || "";
    }
    return { slug: derived.slug, label: label.trim(), source: derived.source };
  }, [
    companies,
    formData.ids_projetos,
    formData.nome,
    formData.id_interno,
    formData.idInterno,
  ]);

  // Helper para exibir "Nome (ID X)"
  const companyDisplayName = (c) => {
    const name = c?.nome || c?.fantasia || c?.razao_social || `Empresa #${c?.id}`;
    return `${name} (ID ${c?.id})`;
  };

  const handleCompaniesChange = (e) => {
    const val = e.target.value;
    const ids = Array.isArray(val) ? val.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0) : [];
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
    setFormData((prev) => ({ ...prev, ids_projetos: ids }));
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {formData?.id ? "Editar Forma de Pagamento" : "Adicionar Forma de Pagamento"}
      </Typography>

      {(errorMessage || isFetchingDetail) && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {isFetchingDetail && <Alert severity="info">Carregando dados…</Alert>}
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Nome */}
          <Grid item xs={12}>
            <TextField
              label="Nome *"
              name="nome"
              value={formData.nome || ""}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ maxLength: 120 }}
              helperText="Ex.: PIX Itaú, Cartão de Crédito Cielo, Dinheiro no balcão"
            />
          </Grid>

          {/* Linha de selects: Empresa, Status, Tipo  */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="payment-method-companies-label">Empresa</InputLabel>
              <Select
                labelId="payment-method-companies-label"
                name="ids_projetos"
                label="Empresa"
                multiple
                value={Array.isArray(formData.ids_projetos) ? formData.ids_projetos : []}
                onChange={handleCompaniesChange}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected.map(Number) : [];
                  if (!ids.length) return "Todas as empresas (global)";
                  const names = companies
                    .filter((c) => ids.includes(Number(c.id)))
                    .map((c) => companyDisplayName(c));
                  return names.length <= 2
                    ? names.join(", ")
                    : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
                }}
              >
                {loadingCompanies && <MenuItem disabled>Carregando empresas…</MenuItem>}
                {!loadingCompanies &&
                  companies.map((c) => {
                    const id = Number(c.id);
                    const checked = (formData.ids_projetos || []).includes(id);
                    return (
                      <MenuItem key={c.id} value={id}>
                        <Checkbox checked={checked} />
                        <ListItemText primary={companyDisplayName(c)} />
                      </MenuItem>
                    );
                  })}
              </Select>
              <FormHelperText>
                Deixe vazio para todas as empresas ou selecione uma ou mais empresas específicas.
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={Number(formData.status) === 1 ? 1 : 0}
                label="Status"
                onChange={handleChange}
                required
              >
                <MenuItem value={1}>Ativo</MenuItem>
                <MenuItem value={0}>Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                name="tipo"
                value={Number(formData.tipo)}
                onChange={(e) => {
                  setCredentialValidation(null);
                  setHasUnsavedChanges(true);
                  const nextTipo = Number(e.target.value);
                  const cielo = deriveCieloUiFromPaymentMethod({ tipo: nextTipo });
                  setFormData((p) => ({
                    ...p,
                    tipo: nextTipo,
                    client_id: "",
                    client_secret: "",
                    urlapi: "",
                    pix_auth_url: "",
                    pix_tls_mode: "",
                    pix_cert_pem: "",
                    pix_key_pem: "",
                    pix_ca_pem: "",
                    pix_pfx_b64: "",
                    pix_pfx_pass: "",
                    cielo_integration_kind: cielo.cielo_integration_kind,
                    cielo_sandbox: cielo.cielo_sandbox,
                    cielo_custom_url: cielo.cielo_custom_url,
                    cielo_pix_provider: cielo.cielo_pix_provider,
                    cielo_detalhes_extra: cielo.cielo_detalhes_extra,
                    appmax_notification_url: "",
                    ...(isPixPaymentTipo(nextTipo) ? {} : payUnpaidFieldsNull()),
                  }));
                }}
                label="Tipo"
              >
                {GROUP_ORDER.flatMap((g) => {
                  const ops = TIPO_OPCOES.filter((o) => o.group === g);
                  if (!ops.length) return [];
                  return [
                    <ListSubheader key={`h-${g}`} disableSticky sx={{ fontWeight: 700 }}>
                      {g}
                    </ListSubheader>,
                    ...ops.map((o) => (
                      <MenuItem key={`op-${g}-${o.value}`} value={o.value}>
                        {o.label}
                      </MenuItem>
                    )),
                  ];
                })}
              </Select>
            </FormControl>
          </Grid>

          {showEmitirBPe ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Number(formData.emitirbpe) === 1}
                    onChange={(_, checked) => {
                      setHasUnsavedChanges(true);
                      setCredentialValidation(null);
                      setFormData((p) => ({ ...p, emitirbpe: checked ? 1 : 0 }));
                    }}
                  />
                }
                label={tSettings("paymentMethodsPage.notification.emitirBpeLabel")}
              />
              <FormHelperText sx={{ mt: -0.5 }}>
                {tSettings("paymentMethodsPage.notification.emitirBpeHelper")}
              </FormHelperText>
            </Grid>
          ) : null}

          {notification?.needed ? (
            <Grid item xs={12}>
              <PaymentMethodNotificationPanel
                notification={notification}
                showPassengerNote={showEmitirBPe && Number(formData.emitirbpe) === 1}
              />
            </Grid>
          ) : null}

          {isAppmaxTipo(formData.tipo) ? (
            <Grid item xs={12}>
              <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {tSettings("paymentMethodsPage.notification.appmax.advancedTitle")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    label={tSettings("paymentMethodsPage.notification.appmax.overrideUrlLabel")}
                    name="appmax_notification_url"
                    value={formData.appmax_notification_url || ""}
                    onChange={handleChange}
                    fullWidth
                    helperText={tSettings("paymentMethodsPage.notification.appmax.overrideUrlHelper")}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          ) : null}

          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.default) === 1}
                  onChange={(_, checked) => {
                    setHasUnsavedChanges(true);
                    setCredentialValidation(null);
                    setFormData((p) => ({ ...p, default: checked ? 1 : 0 }));
                  }}
                />
              }
              label="Default"
            />
          </Grid>

          {/* Id interno */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Id Interno"
              name="id_interno"
              value={formData.id_interno || ""}
              onChange={handleChange}
              fullWidth
              helperText="Código interno para integrações/relatórios (opcional)."
            />
          </Grid>

          {/* Valores e taxas */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Acréscimo (%)"
              name="acrescimo"
              type="number"
              value={formData.acrescimo ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Taxa de retenção (%) em estorno"
              name="taxa_retencao"
              type="number"
              value={formData.taxa_retencao ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Desconto (%)"
              name="desconto"
              type="number"
              value={formData.desconto ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Parcelas sem juros"
              name="parcelas_semjuros"
              type="number"
              value={formData.parcelas_semjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Parcelas com juros"
              name="parcelas_comjuros"
              type="number"
              value={formData.parcelas_comjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tipo-calculo-juros-label">
                {tSettings("paymentMethodsPage.tipoCalculoJurosLabel")}
              </InputLabel>
              <Select
                labelId="tipo-calculo-juros-label"
                name="tipo_calculo_juros"
                value={formData.tipo_calculo_juros === "total" ? "total" : "ao_mes"}
                label={tSettings("paymentMethodsPage.tipoCalculoJurosLabel")}
                onChange={handleChange}
              >
                <MenuItem value="ao_mes">
                  {tSettings("paymentMethodsPage.tipoCalculoJurosAoMes")}
                </MenuItem>
                <MenuItem value="total">
                  {tSettings("paymentMethodsPage.tipoCalculoJurosTotal")}
                </MenuItem>
              </Select>
              <FormHelperText>
                {formData.tipo_calculo_juros === "total"
                  ? tSettings("paymentMethodsPage.tipoCalculoJurosTotalHelper")
                  : tSettings("paymentMethodsPage.tipoCalculoJurosAoMesHelper")}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={
                formData.tipo_calculo_juros === "total"
                  ? tSettings("paymentMethodsPage.jurosParcelamentoTotalLabel")
                  : tSettings("paymentMethodsPage.jurosParcelamentoAoMesLabel")
              }
              name="juros_parcelamento"
              type="number"
              value={formData.juros_parcelamento ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
              helperText={
                formData.tipo_calculo_juros === "total"
                  ? tSettings("paymentMethodsPage.jurosParcelamentoTotalHelper")
                  : tSettings("paymentMethodsPage.jurosParcelamentoAoMesHelper")
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Valor mínimo da parcela sem juros (R$)"
              name="valor_minimo_parcela_semjuros"
              type="number"
              value={formData.valor_minimo_parcela_semjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Opcional. Em branco, o canal usa o padrão (ex.: R$ 100 no balcão)."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Valor mínimo da parcela com juros (R$)"
              name="valor_minimo_parcela_comjuros"
              type="number"
              value={formData.valor_minimo_parcela_comjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Opcional. Em branco, o canal usa o padrão (ex.: R$ 100 no balcão)."
            />
          </Grid>

          {isCardPaymentForm && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Bandeiras aceitas
              </Typography>
              <FormHelperText sx={{ mx: 0, mt: 0.25, mb: 1 }}>
                Selecione as bandeiras exibidas no rodapé quando este meio de pagamento estiver ativo.
              </FormHelperText>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {PAYMENT_CARD_BRAND_OPTIONS.map((brand) => {
                  const checked = (formData.bandeiras_aceitas || []).includes(brand.slug);
                  return (
                    <FormControlLabel
                      key={brand.slug}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleBandeiraToggle(brand.slug)}
                        />
                      }
                      label={brand.label}
                    />
                  );
                })}
              </Stack>
            </Grid>
          )}

          {/* ======= Mapeamento de tipos por status do pagamento ======= */}
          {cascadeBases.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Cada bloco abaixo corresponde a um momento distinto do fluxo de pagamento.
                  Configure tipo e subtipo para que pedidos e automações reajam corretamente em cada situação.
                </Typography>
              </Grid>
              {cascadeBases.map((base) => renderCascade(base))}
              <Divider sx={{ width: "100%", my: 2 }} />
            </>
          )}

          {rules.cieloIntegration && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  {isPixCieloForm
                    ? tSettings("paymentMethodsPage.cieloPixSectionTitle")
                    : "Integração Cielo"}
                </Typography>
                <FormHelperText sx={{ mx: 0, mt: 0.25 }}>{cieloEffectiveBaseHint}</FormHelperText>
                {isPixCieloForm && (
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    {tSettings("paymentMethodsPage.cieloPixSectionScope")}
                  </FormHelperText>
                )}
                {String(formData.cielo_integration_kind || "") === "ec_portal" && (
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    {tSettings("paymentMethodsPage.cieloPortalOauthCredHint")}
                  </FormHelperText>
                )}
                {String(formData.cielo_integration_kind || "") === "pix_api2" && (
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    {tSettings("paymentMethodsPage.cieloPixApi2CredHint")}
                  </FormHelperText>
                )}
                {String(formData.cielo_integration_kind || "") === "bradesco_pix" && (
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    {tSettings("paymentMethodsPage.cieloPixBradescoCredHint")}
                  </FormHelperText>
                )}
                {isPixCieloForm &&
                  ["ec_api", "ec_portal"].includes(
                    String(formData.cielo_integration_kind || "")
                  ) && (
                    <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                      {tSettings("paymentMethodsPage.cieloPixEcommerceModalityHint")}
                    </FormHelperText>
                  )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="cielo-integration-label">
                    {isPixCieloForm
                      ? tSettings("paymentMethodsPage.cieloPixKindLabel")
                      : "Tipo de integração"}
                  </InputLabel>
                  <Select
                    labelId="cielo-integration-label"
                    name="cielo_integration_kind"
                    value={normalizeCieloIntegrationKindSelectValue(
                      formData.cielo_integration_kind,
                      isPixCieloForm
                    )}
                    label={
                      isPixCieloForm
                        ? tSettings("paymentMethodsPage.cieloPixKindLabel")
                        : "Tipo de integração"
                    }
                    onChange={(e) => {
                      setHasUnsavedChanges(true);
                      setCredentialValidation(null);
                      setFormData((prev) => ({
                        ...prev,
                        cielo_integration_kind: e.target.value,
                      }));
                    }}
                  >
                    {isPixCieloForm ? (
                      [
                        <MenuItem key="ec_api" value="ec_api">
                          {tSettings("paymentMethodsPage.cieloPixKind_ec_api")}
                        </MenuItem>,
                        <MenuItem key="ec_portal" value="ec_portal">
                          {tSettings("paymentMethodsPage.cieloPixKind_ec_portal")}
                        </MenuItem>,
                        <MenuItem key="gateway" value="gateway">
                          {tSettings("paymentMethodsPage.cieloPixKind_gateway")}
                        </MenuItem>,
                        <MenuItem key="pix_api2" value="pix_api2">
                          {tSettings("paymentMethodsPage.cieloPixKind_pix_api2")}
                        </MenuItem>,
                        <MenuItem key="bradesco_pix" value="bradesco_pix">
                          {tSettings("paymentMethodsPage.cieloPixKind_bradesco_pix")}
                        </MenuItem>,
                      ]
                    ) : (
                      [
                        <MenuItem key="ec_api" value="ec_api">
                          E-commerce — API direta (api.cieloecommerce…)
                        </MenuItem>,
                        <MenuItem key="ec_portal" value="ec_portal">
                          E-commerce — API transacional /api/public (OAuth)
                        </MenuItem>,
                        <MenuItem key="gateway" value="gateway">
                          Gateway — Braspag (api.braspag…)
                        </MenuItem>,
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {isPixCieloForm &&
                String(formData.cielo_integration_kind || "") === "gateway" && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="cielo-pix-provider-label">
                        {tSettings("paymentMethodsPage.cieloPixProviderLabel")}
                      </InputLabel>
                      <Select
                        labelId="cielo-pix-provider-label"
                        name="cielo_pix_provider"
                        value={normalizeCieloPixGatewayProvider(formData.cielo_pix_provider)}
                        label={tSettings("paymentMethodsPage.cieloPixProviderLabel")}
                        onChange={(e) => {
                          setHasUnsavedChanges(true);
                          setCredentialValidation(null);
                          setFormData((prev) => ({
                            ...prev,
                            cielo_pix_provider: normalizeCieloPixGatewayProvider(e.target.value),
                          }));
                        }}
                      >
                        {CIELO_PIX_GATEWAY_PROVIDERS.map((p) => (
                          <MenuItem key={p} value={p}>
                            {tSettings(`paymentMethodsPage.cieloPixProvider_${p}`)}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText sx={{ mx: 0 }}>
                        {tSettings("paymentMethodsPage.cieloPixProviderHelper")}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="cielo-sandbox-label">
                    {isPixCieloForm
                      ? tSettings("paymentMethodsPage.cieloPixAmbienteLabel")
                      : "Ambiente"}
                  </InputLabel>
                  <Select
                    labelId="cielo-sandbox-label"
                    name="cielo_sandbox"
                    value={Number(formData.cielo_sandbox)}
                    label={
                      isPixCieloForm
                        ? tSettings("paymentMethodsPage.cieloPixAmbienteLabel")
                        : "Ambiente"
                    }
                    onChange={(e) => {
                      setHasUnsavedChanges(true);
                      setCredentialValidation(null);
                      setFormData((prev) => ({
                        ...prev,
                        cielo_sandbox: Number(e.target.value),
                      }));
                    }}
                  >
                    <MenuItem value={0}>
                      {isPixCieloForm
                        ? tSettings("paymentMethodsPage.cieloPixAmbiente_prod")
                        : "Produção"}
                    </MenuItem>
                    <MenuItem value={1}>
                      {isPixCieloForm
                        ? tSettings("paymentMethodsPage.cieloPixAmbiente_sandbox")
                        : "Homologação (sandbox)"}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formData.cielo_custom_url) === 1}
                      onChange={(_, checked) => {
                        setHasUnsavedChanges(true);
                        setCredentialValidation(null);
                        setFormData((p) => ({
                          ...p,
                          cielo_custom_url: checked ? 1 : 0,
                          urlapi: checked ? p.urlapi : "",
                        }));
                      }}
                    />
                  }
                  label={
                    isPixCieloForm
                      ? tSettings("paymentMethodsPage.cieloPixCustomUrlLabel")
                      : "Usar endereço da API personalizado"
                  }
                />
                <FormHelperText sx={{ ml: 1.75, mt: -0.5 }}>
                  {isPixCieloForm
                    ? tSettings("paymentMethodsPage.cieloPixCustomUrlHelper")
                    : "Se desmarcado, o sistema usa o endereço padrão do tipo e do ambiente acima."}
                </FormHelperText>
              </Grid>
              {Number(formData.cielo_custom_url) === 1 && (
                <Grid item xs={12}>
                  <TextField
                    label={labelFor("urlapi", "Endereço da API")}
                    name="urlapi"
                    value={formData.urlapi}
                    onChange={handleChange}
                    fullWidth
                    helperText={rules.helpers?.urlapi}
                  />
                </Grid>
              )}
            </>
          )}

          {/* Campos por tipo */}
          {rules.show.has("client_id") && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = hasStoredSecret("client_id");
                const isMasked = hasStored && !formData.client_id;
                return (
              <TextField
                label={labelFor("client_id", "Client ID")}
                name="client_id"
                    value={isMasked ? "••••••••" : formData.client_id}
                    onChange={isMasked ? undefined : handleChange}
                    onClick={isMasked ? () => maskSecretField("client_id") : undefined}
                    onFocus={isMasked ? () => maskSecretField("client_id") : undefined}
                fullWidth
                required={isFieldRequired("client_id")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.client_id && hasStoredSecret("client_id")
                    ? "Já existe um Client ID configurado. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("client_secret") && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = hasStoredSecret("client_secret");
                const isMasked = hasStored && !formData.client_secret;
                return (
              <TextField
                label={labelFor("client_secret", "Client Secret")}
                name="client_secret"
                    value={isMasked ? "••••••••" : formData.client_secret}
                    onChange={isMasked ? undefined : handleChange}
                fullWidth
                type="password"
                autoComplete="new-password"
                required={isFieldRequired("client_secret")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.client_secret && hasStoredSecret("client_secret")
                    ? "Já existe um Client Secret configurado. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("client_secret") : undefined}
                    onFocus={isMasked ? () => maskSecretField("client_secret") : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("pix_key") && (
            <Grid item xs={12}>
              {(() => {
                const hasStored = hasStoredSecret("pix_key");
                const isMasked = hasStored && !formData.pix_key;
                return (
              <TextField
                label={labelFor("pix_key", "Pix Key")}
                name="pix_key"
                    value={isMasked ? "••••••••" : formData.pix_key}
                    onChange={isMasked ? undefined : handleChange}
                fullWidth
                required={isFieldRequired("pix_key")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.pix_key && hasStoredSecret("pix_key")
                    ? "Já existe uma Pix Key configurada. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("pix_key") : undefined}
                    onFocus={isMasked ? () => maskSecretField("pix_key") : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("pix_merchant_name") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_merchant_name", "Pix Merchant Name")}
                name="pix_merchant_name"
                value={formData.pix_merchant_name}
                onChange={handleChange}
                fullWidth
                required={rules.required.includes("pix_merchant_name")}
              />
            </Grid>
          )}

          {rules.show.has("pix_merchant_city") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_merchant_city", "Pix Merchant City")}
                name="pix_merchant_city"
                value={formData.pix_merchant_city}
                onChange={handleChange}
                fullWidth
                required={rules.required.includes("pix_merchant_city")}
              />
            </Grid>
          )}

          {/* URL base (Cielo / PIX Itaú / Adyen / AppMax) */}
          {rules.show.has("urlapi") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("urlapi", "URL da API")}
                name="urlapi"
                value={formData.urlapi}
                onChange={handleChange}
                fullWidth
                helperText={
                  Number(formData.tipo) === 3
                    ? tSettings("paymentMethodsPage.cieloCheckoutUrlapiHelper")
                    : rules.helpers?.urlapi || ""
                }
              />
            </Grid>
          )}

          {rules.show.has("pix_auth_url") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_auth_url", "URL do OAuth (opcional)")}
                name="pix_auth_url"
                value={formData.pix_auth_url}
                onChange={handleChange}
                fullWidth
                helperText="Se vazio, o backend tentará /api/oauth/token e /oauth/token no host acima."
              />
            </Grid>
          )}

          {/* Pix Cielo api2 homologação: sem certificado mTLS (Bradesco exige sempre) */}
          {isCieloPixApi2 && !isCieloPixProd && (
            <Grid item xs={12}>
              <Alert severity="info">
                {tSettings("paymentMethodsPage.cieloPixApi2MtlsSandboxSkip")}
              </Alert>
            </Grid>
          )}
          {isBradescoPix && (
            <Grid item xs={12}>
              <Alert severity="info">
                {tSettings("paymentMethodsPage.cieloPixBradescoMtlsAlways")}
              </Alert>
            </Grid>
          )}

          {/* ===== TLS PIX ITAÚ / mTLS Cielo API Pix / Bradesco ===== */}
          {showPixMtlsPanel && !(isCieloPixApi2 && !isCieloPixProd) && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  ...(showCieloStyleMtls
                    ? { bgcolor: "grey.50", borderColor: "grey.200" }
                    : {}),
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
                  {isCieloPixApi2
                    ? tSettings("paymentMethodsPage.cieloPixApi2MtlsTitle")
                    : isBradescoPix
                      ? tSettings("paymentMethodsPage.cieloPixBradescoMtlsTitle")
                      : tSettings("paymentMethodsPage.pixItauCertTitle")}
                </Typography>
                {isCieloPixProd && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {tSettings("paymentMethodsPage.cieloPixApi2MtlsSubtitle")}
                  </Typography>
                )}
                {showBradescoMtls && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {tSettings("paymentMethodsPage.cieloPixBradescoMtlsSubtitle")}
                  </Typography>
                )}

                {isPixItau && (
                <RadioGroup
                  row
                  name="pix_tls_mode"
                  value={formData.pix_tls_mode || ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      pix_tls_mode: e.target.value,
                      // limpa pares ao trocar de modo
                      pix_cert_pem: "",
                      pix_key_pem: "",
                      pix_ca_pem: "",
                      pix_pfx_b64: "",
                      pix_pfx_pass: "",
                    }))
                  }
                >
                  <FormControlLabel
                    value="pem"
                    control={<Radio />}
                    label={tSettings("paymentMethodsPage.pixItauModeTextOrFiles")}
                  />
                  <FormControlLabel
                    value="pfx"
                    control={<Radio />}
                    label={tSettings("paymentMethodsPage.pixItauModePfxOnly")}
                  />
                </RadioGroup>
                )}

                {isCieloPixProd && (
                  <CieloPixMtlsCsrPanel
                    paymentMethodId={formData.id || null}
                    suggestedPartnerSlug={cieloMtlsPartner.slug}
                    suggestedPartnerLabel={cieloMtlsPartner.label}
                    hasStoredKey={!!formData.has_pix_key_pem}
                    hasStoredCert={
                      !!formData.has_pix_cert_pem ||
                      String(formData.pix_cert_pem || "").trim() !== ""
                    }
                    hasStoredCsr={!!formData.has_cielo_pix_mtls_csr}
                    onPrivateKeyPem={(pem, meta) => {
                      setHasUnsavedChanges(true);
                      setCredentialValidation(null);
                      setFormData((prev) => {
                        const extra = {
                          ...(prev.cielo_detalhes_extra &&
                          typeof prev.cielo_detalhes_extra === "object"
                            ? prev.cielo_detalhes_extra
                            : {}),
                        };
                        if (meta?.csrPem) {
                          extra.cieloPixMtlsCsrPem = meta.csrPem;
                          extra.cieloPixMtlsCsrGeneratedAt =
                            meta.generatedAt || new Date().toISOString();
                          if (meta.cn) extra.cieloPixMtlsCn = meta.cn;
                          if (meta.partnerSlug) {
                            extra.cieloPixMtlsPartnerSlug = meta.partnerSlug;
                          }
                          if (meta.csrSource) {
                            extra.cieloPixMtlsCsrSource = meta.csrSource;
                          }
                        }
                        return {
                          ...prev,
                          ...(pem ? { pix_key_pem: pem } : {}),
                          has_pix_key_pem: !!(
                            meta?.storedOnServer ||
                            pem ||
                            prev.has_pix_key_pem
                          ),
                          has_cielo_pix_mtls_csr: !!(
                            meta?.hasCsr ||
                            meta?.storedOnServer ||
                            meta?.csrPem ||
                            prev.has_cielo_pix_mtls_csr
                          ),
                          cielo_detalhes_extra: extra,
                          ...(meta?.certCleared
                            ? { pix_cert_pem: "", has_pix_cert_pem: false }
                            : {}),
                        };
                      });
                    }}
                  />
                )}

                {(isCieloPixProd ||
                  showBradescoMtls ||
                  (isPixItau && formData.pix_tls_mode === "pem")) && (
                  <Stack spacing={2} sx={{ mt: showCieloStyleMtls ? 2.5 : 1 }}>
                    {isCieloPixProd ? (
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {tSettings("paymentMethodsPage.cieloPixStep3Title")}
                      </Typography>
                    ) : null}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {tSettings("paymentMethodsPage.pixCertHow")}
                      </Typography>
                      <RadioGroup
                        row
                        value={pixCertInput}
                        onChange={(e) => setPixCertInput(e.target.value)}
                      >
                        <FormControlLabel
                          value="files"
                          control={<Radio />}
                          label={tSettings("paymentMethodsPage.pixCertHowFiles")}
                        />
                        <FormControlLabel
                          value="text"
                          control={<Radio />}
                          label={tSettings("paymentMethodsPage.pixCertHowText")}
                        />
                      </RadioGroup>
                      <FormHelperText sx={{ mx: 0 }}>
                        {pixCertInput === "files"
                          ? tSettings("paymentMethodsPage.pixCertHowFilesHint")
                          : tSettings("paymentMethodsPage.pixCertHowTextHint")}
                      </FormHelperText>
                    </Box>
                    {pixCertInput === "files" ? (
                      <CieloPixCertFilesPanel
                        onConverted={({ certPem, keyPem }) => {
                          setHasUnsavedChanges(true);
                          setCredentialValidation(null);
                          setFormData((prev) => ({
                            ...prev,
                            pix_tls_mode: "pem",
                            pix_cert_pem: certPem,
                            pix_key_pem: keyPem,
                          }));
                        }}
                      />
                    ) : (
                    <>
                    <TextField
                      label={tSettings("paymentMethodsPage.pixCertTextCertLabel")}
                      name="pix_cert_pem"
                      value={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? "••••••••"
                            : formData.pix_cert_pem
                        }
                        onChange={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? undefined
                            : (e) => setFormData((p) => ({ ...p, pix_cert_pem: e.target.value }))
                        }
                      fullWidth
                      multiline
                      minRows={6}
                      helperText={
                        hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                          ? tSettings("paymentMethodsPage.cieloPixCertConfigured")
                          : tSettings("paymentMethodsPage.pixCertTextCertHelper")
                      }
                        InputProps={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? () => maskSecretField("pix_cert_pem")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? () => maskSecretField("pix_cert_pem")
                            : undefined
                        }
                    />
                    <TextField
                      label={tSettings("paymentMethodsPage.pixCertTextKeyLabel")}
                      name="pix_key_pem"
                      value={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? "••••••••"
                            : formData.pix_key_pem
                        }
                        onChange={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? undefined
                            : (e) => setFormData((p) => ({ ...p, pix_key_pem: e.target.value }))
                        }
                      fullWidth
                      multiline
                      minRows={6}
                      helperText={
                        hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                          ? tSettings("paymentMethodsPage.pixCertTextKeyConfigured")
                          : tSettings("paymentMethodsPage.pixCertTextKeyHelper")
                      }
                        InputProps={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? () => maskSecretField("pix_key_pem")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? () => maskSecretField("pix_key_pem")
                            : undefined
                        }
                    />
                    {(isPixItau || isCieloPixProd) ? (
                    <TextField
                      label={tSettings("paymentMethodsPage.pixItauCaLabel")}
                      name="pix_ca_pem"
                      value={formData.pix_ca_pem}
                      onChange={(e) => setFormData((p) => ({ ...p, pix_ca_pem: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={4}
                      helperText={tSettings("paymentMethodsPage.pixItauCaHelper")}
                    />
                    ) : null}
                    </>
                    )}
                  </Stack>
                )}

                {isPixItau && formData.pix_tls_mode === "pfx" && (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button component="label" variant="outlined" sx={{ textTransform: "none" }}>
                        {tSettings("paymentMethodsPage.pixItauPfxButton")}
                        <input
                          type="file"
                          accept=".pfx,.p12,application/x-pkcs12"
                          hidden
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const b64 = await handleFileToBase64(f);
                            setHasUnsavedChanges(true);
                            setCredentialValidation(null);
                            setFormData((p) => ({ ...p, pix_pfx_b64: b64 }));
                          }}
                        />
                      </Button>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {formData.pix_pfx_b64
                          ? tSettings("paymentMethodsPage.pixItauPfxLoaded")
                          : tSettings("paymentMethodsPage.pixItauPfxEmpty")}
                      </Typography>
                    </Stack>
                    <TextField
                      label={tSettings("paymentMethodsPage.pixItauPfxPassword")}
                      name="pix_pfx_pass"
                      value={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? "••••••••"
                            : formData.pix_pfx_pass
                        }
                        onChange={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? undefined
                            : (e) => {
                              setHasUnsavedChanges(true);
                              setCredentialValidation(null);
                              setFormData((p) => ({ ...p, pix_pfx_pass: e.target.value }));
                            }
                        }
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      helperText={
                            hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                              ? "Valor já configurado. Clique para alterar."
                              : !formData.pix_pfx_pass && hasStoredSecret("pix_pfx_pass")
                          ? "Já existe uma senha de PFX configurada. Preencha apenas se quiser trocar."
                          : undefined
                      }
                        InputProps={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? () => maskSecretField("pix_pfx_pass")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? () => maskSecretField("pix_pfx_pass")
                            : undefined
                        }
                    />
                    <TextField
                      label="CA PEM (opcional – cadeia intermediária)"
                      name="pix_ca_pem"
                      value={formData.pix_ca_pem}
                      onChange={(e) => setFormData((p) => ({ ...p, pix_ca_pem: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Stack>
                )}
              </Paper>
            </Grid>
          )}

          {providerDocConfig && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  mt: 1,
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}
                >
                  {tSettings(providerDocConfig.sectionTitleKey)}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {tSettings(providerDocConfig.titleKey)}
                </Typography>
                {providerDocConfig.bulletKeys.map((key) => (
                  <Typography key={key} variant="body2" component="p" sx={{ mb: 0.75 }}>
                    {tSettings(key)}
                  </Typography>
                ))}
                {providerDocConfig.links.length > 0 && (
                  <Stack component="ul" spacing={0.5} sx={{ pl: 2.25, mb: 0, mt: 1 }}>
                    {providerDocConfig.links.map((l) => (
                      <Typography
                        key={l.href}
                        component="li"
                        variant="body2"
                        sx={{ display: "list-item" }}
                      >
                        <Link
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 500 }}
                        >
                          {tSettings(l.labelKey)}
                        </Link>
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Alert>
            </Grid>
          )}
        </Grid>

        {credentialValidation && (
          <Box
            ref={credentialValidationAnchorRef}
            sx={{ mt: 3, width: "100%" }}
            id="payment-method-credential-validation"
          >
            <Alert severity={credentialValidation.severity || (credentialValidation.valid ? "success" : "warning")}>
              <Typography variant="subtitle2" component="span" display="block" gutterBottom>
                {credentialValidation.title}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {credentialValidation.detail}
              </Typography>
              {Array.isArray(credentialValidation.nextSteps) && credentialValidation.nextSteps.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Como resolver
                  </Typography>
                  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                    {credentialValidation.nextSteps.map((step, idx) => (
                      <Typography key={idx} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {step}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {credentialValidation.technicalDetail ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                  Detalhe para suporte: {credentialValidation.technicalDetail}
                </Typography>
              ) : null}
            </Alert>
          </Box>
        )}

        {/* Rodapé */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 2, mt: credentialValidation ? 2 : 3 }}>
          {podeTestarCredenciais && (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleValidateCredentials}
              disabled={
                hasUnsavedChanges ||
                isValidatingCredentials ||
                isSubmitting ||
                isLoading ||
                isFetchingDetail
              }
              sx={{ mr: "auto" }}
            >
              {isValidatingCredentials ? <CircularProgress size={20} /> : "Testar credenciais"}
            </Button>
          )}
          {podeTestarCredenciais && hasUnsavedChanges && (
            <Typography variant="caption" sx={{ mr: "auto", alignSelf: "center", opacity: 0.75 }}>
              Salve as alterações para poder testar as credenciais.
            </Typography>
          )}
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting || isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isSubmitting || isLoading ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </Box>

        {/* Exibição agregada de erros (opcional) */}
        {errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Alert>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default AddPaymentMethodForm;
